/* The Marketplace, rebuilt from the authoritative artboard in `Lumiq App.dc.html`.
 *
 * **This screen deliberately does not use the shared kit**, and that is why the previous version
 * was replaced. `PageHeader`, `Alert`, `EmptyState`, `StatusBadge`, `Drawer` and `ConfirmDialog`
 * each render a different shape from the reference — a 27px/800 heading, a 3px-spined banner, a
 * 44px centred empty state, a 6px-dot pill, a right-aligned scrim drawer, and an *inline* uninstall
 * confirmation. Substituting them changed the visual result on every tab. Local markup, `lq-mp__`
 * classes, values read out of the artboard rather than approximated.
 *
 * **The run detail is an inline panel, not an overlay** — the artboard's "← Back to runs" is the
 * tell, and it replaces the list in place. Only the plugin detail and the listing detail are
 * drawers, so there are two, not three.
 *
 * **The artboard's four-button role switcher is not implemented.** It exists so the four role
 * experiences appear on one canvas; shipping it would be a control that lets somebody claim a role.
 * The viewer line beneath the lede is rendered from real `useIdentity()` permissions instead.
 */

import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  can,
  INSTALL_PLUGIN_PERMISSION,
  PUBLISH_PLUGIN_PERMISSION,
  REVIEW_PLUGIN_PERMISSION,
  RUN_PLUGIN_PERMISSION,
  SUBMIT_PLUGIN_PERMISSION,
  useIdentity,
} from '@/entities/session';
import { useAuditEntries } from '@/features/audit';
import type { Capability, Plugin, PluginRun } from '@/shared/api/generated/marketplace';

import {
  REGISTRY_LIMIT,
  useCapabilityCatalogue,
  useInstallations,
  useMyListings,
  usePlugins,
  useRunFindings,
  useRuns,
  useSetInstallationEnabled,
  useSubmitPlugin,
  useUninstallPlugin,
} from '../api/marketplace';
import {
  capabilityLabelKey,
  groupByName,
  installationFor,
  isNarrowed,
  isThinRun,
  matchesQuery,
  runBlockedReason,
  runTone,
  stateTone,
  TAB_IDS,
  upgradeTargetFor,
  verdictTone,
} from '../model/marketplace';
import type { TabId } from '../model/marketplace';
import { Banner, Empty, Pill } from './atoms';
import type { PillTone } from './atoms';
import { ListingDrawer } from './ListingDrawer';
import { PluginDrawer } from './PluginDrawer';

const PILL_FOR = {
  ok: 'ok',
  unknown: 'unknown',
  blocked: 'error',
  live: 'ok',
  progressing: 'info',
} satisfies Record<string, PillTone>;

export interface Permissions {
  readonly submit: boolean;
  readonly review: boolean;
  readonly publish: boolean;
  readonly install: boolean;
  readonly run: boolean;
}

export function MarketplaceScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const [tab, setTab] = useState<TabId>('marketplace');
  const [openPlugin, setOpenPlugin] = useState<string | null>(null);
  const [openListing, setOpenListing] = useState<string | null>(null);

  const plugins = usePlugins();
  const installations = useInstallations();
  const catalogue = useCapabilityCatalogue();

  const may: Permissions = {
    submit: can(identity.data, SUBMIT_PLUGIN_PERMISSION),
    review: can(identity.data, REVIEW_PLUGIN_PERMISSION),
    publish: can(identity.data, PUBLISH_PLUGIN_PERMISSION),
    install: can(identity.data, INSTALL_PLUGIN_PERMISSION),
    run: can(identity.data, RUN_PLUGIN_PERMISSION),
  };

  /* The whole router is unmounted without wasmtime, so every request 404s together. Its own state:
   * "no plugin runtime" is a deployment fact an operator can act on, where "could not read the
   * registry" sends them looking for an outage that is not there. */
  const noRuntime =
    plugins.isError &&
    /404|not found/i.test((plugins.error as Error | null)?.message ?? '');

  const held = (Object.keys(may) as (keyof Permissions)[]).filter((name) => may[name]);

  return (
    <div className="lq-mp">
      <div className="lq-mp__head">
        <h1 className="lq-mp__h1">{t('marketplace.title')}</h1>
        <p className="lq-mp__lede">{t('marketplace.intro')}</p>
      </div>

      <div className="lq-mp__viewer">
        {held.length === 0
          ? t('marketplace.viewer.readOnly')
          : t('marketplace.viewer.holding', { actions: held.join(', ') })}
      </div>

      <nav className="lq-mp__tabs" aria-label={t('marketplace.tabs.label')}>
        {TAB_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={id === tab ? 'lq-mp__tab lq-mp__tab--on' : 'lq-mp__tab'}
            aria-current={id === tab ? 'page' : undefined}
            onClick={() => {
              setTab(id);
            }}
          >
            {t(`marketplace.tab.${id}`)}
          </button>
        ))}
      </nav>

      {noRuntime ? (
        <Empty
          title={t('marketplace.noRuntime.title')}
          body={t('marketplace.noRuntime.guidance')}
        />
      ) : (
        <>
          {tab === 'marketplace' ? (
            <Discovery
              plugins={plugins}
              installations={installations}
              catalogue={catalogue}
              onOpen={setOpenPlugin}
            />
          ) : null}
          {tab === 'installed' ? (
            <Installed
              plugins={plugins}
              installations={installations}
              may={may}
              onOpen={setOpenPlugin}
            />
          ) : null}
          {tab === 'runs' ? <Runs plugins={plugins} may={may} /> : null}
          {tab === 'listings' ? <Listings may={may} onOpen={setOpenListing} /> : null}
          {tab === 'review' ? <ReviewQueue may={may} onOpen={setOpenListing} /> : null}
          {tab === 'activity' ? <Activity /> : null}
        </>
      )}

      {openPlugin !== null ? (
        <PluginDrawer
          pluginId={openPlugin}
          may={may}
          onClose={() => {
            setOpenPlugin(null);
          }}
        />
      ) : null}
      {openListing !== null ? (
        <ListingDrawer
          pluginId={openListing}
          may={may}
          onClose={() => {
            setOpenListing(null);
          }}
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- boundary card */

function Boundary({
  catalogue,
}: {
  readonly catalogue: ReturnType<typeof useCapabilityCatalogue>;
}): ReactElement | null {
  const { t } = useTranslation();

  if (catalogue.isError) {
    return (
      <Banner
        title={t('marketplace.boundary.unreadable')}
        body={t('marketplace.boundary.unreadableBody')}
      />
    );
  }
  if (catalogue.data === undefined) return null;

  return (
    <div className="lq-mp__boundaryCard">
      <div className="lq-mp__eyebrow">{t('marketplace.boundary.title')}</div>
      <blockquote className="lq-mp__quote">{`"${catalogue.data.sandbox}"`}</blockquote>
      <div className="lq-mp__grid2">
        <div>
          <div className="lq-mp__subTitle">{t('marketplace.boundary.never')}</div>
          {catalogue.data.never_granted.map((entry) => (
            <div className="lq-mp__never" key={entry}>
              <span className="lq-mp__dash">—</span>
              <span>{entry}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="lq-mp__subTitle">{t('marketplace.boundary.granted')}</div>
          {catalogue.data.capabilities.map((entry) => (
            <div className="lq-mp__capRow" key={entry.code}>
              <div className="lq-mp__capCode">{entry.code}</div>
              <div className="lq-mp__capDesc">{t(capabilityLabelKey(entry.code))}</div>
              <div className="lq-mp__capGate">
                {t('marketplace.boundary.gatedBy')}{' '}
                <span className="lq-mp__mono">{entry.governed_by}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- tab 1: discovery */

function Discovery({
  plugins,
  installations,
  catalogue,
  onOpen,
}: {
  readonly plugins: ReturnType<typeof usePlugins>;
  readonly installations: ReturnType<typeof useInstallations>;
  readonly catalogue: ReturnType<typeof useCapabilityCatalogue>;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const installed = installations.data ?? [];
  const loaded = plugins.data ?? [];
  const matching = loaded.filter((each) => matchesQuery(each, query));
  const groups = groupByName(matching);
  // Counted over the whole loaded registry, never over the filtered set: "12 of 44 are
  // installable" is a fact about the registry, and recomputing it per search would make the
  // sentence change meaning without changing its words.
  const published = loaded.filter((each) => each.state === 'published').length;

  return (
    <>
      {plugins.isError ? (
        <Banner
          title={t('marketplace.error.registry')}
          body={t('marketplace.error.registryBody')}
        />
      ) : null}

      <Boundary catalogue={catalogue} />

      <div className="lq-mp__summary">
        {t('marketplace.summary', { published, total: loaded.length })}
      </div>

      {loaded.length === REGISTRY_LIMIT ? (
        <div className="lq-mp__summary">
          {t('marketplace.registryTruncated', { limit: REGISTRY_LIMIT })}
        </div>
      ) : null}

      <form
        className="lq-mp__search"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft);
        }}
      >
        <label className="lq-mp__label" htmlFor="lq-mp-search">
          {t('marketplace.search.label')}
        </label>
        <input
          id="lq-mp-search"
          className="lq-mp__searchInput"
          type="search"
          value={draft}
          placeholder={t('marketplace.search.placeholder')}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <button type="submit" className="lq-mp__btn lq-mp__btn--md">
          {t('marketplace.search.action')}
        </button>
        {query.trim() === '' ? null : (
          <button
            type="button"
            className="lq-mp__btn"
            onClick={() => {
              setDraft('');
              setQuery('');
            }}
          >
            {t('marketplace.search.clear')}
          </button>
        )}
      </form>

      {/* The filtered count is its own line rather than folded into the summary above, because
          the two are different facts: how much of the registry is installable, and how much of it
          this screen is currently showing. */}
      {query.trim() === '' ? null : (
        <div className="lq-mp__searchNote">
          {t('marketplace.search.showing', {
            shown: matching.length,
            total: loaded.length,
            query: query.trim(),
          })}
        </div>
      )}

      {plugins.data !== undefined && groups.length === 0 ? (
        query.trim() === '' ? (
          <Empty
            title={t('marketplace.empty.title')}
            body={t('marketplace.empty.guidance')}
          />
        ) : (
          <Empty
            title={t('marketplace.search.noMatch', { query: query.trim() })}
            body={t('marketplace.search.noMatchBody')}
          />
        )
      ) : null}

      {groups.map((group) => (
        <div className="lq-mp__group" key={group.name}>
          <div className="lq-mp__groupHead">
            <span className="lq-mp__groupName">{group.name}</span>
            {group.versions.length > 1 ? (
              <span className="lq-mp__small">
                {t('marketplace.versionCount', { count: group.versions.length })}
              </span>
            ) : null}
          </div>
          {group.versions.map((plugin) => {
            const installation = installationFor(plugin, installed);
            return (
              <button
                type="button"
                className="lq-mp__row"
                key={plugin.id}
                onClick={() => {
                  onOpen(plugin.id);
                }}
              >
                <span className="lq-mp__ver">v{plugin.version}</span>
                <span className="lq-mp__hash" title={plugin.artifact_hash}>
                  {plugin.artifact_hash.slice(0, 12)}
                </span>
                <Pill
                  tone={PILL_FOR[verdictTone(plugin.review_verdict)]}
                  label={t(`marketplace.verdict.${plugin.review_verdict}`)}
                />
                {installation === undefined ? (
                  <Pill
                    tone={PILL_FOR[stateTone(plugin.state)]}
                    label={t(`marketplace.state.${plugin.state}`)}
                  />
                ) : (
                  <Pill
                    tone={installation.enabled ? 'ok' : 'unknown'}
                    label={
                      installation.enabled
                        ? t('marketplace.install.enabled')
                        : t('marketplace.install.disabled')
                    }
                  />
                )}
                {isNarrowed(plugin, installation) ? (
                  <span className="lq-mp__small">{t('marketplace.narrowed')}</span>
                ) : null}
                <span className="lq-mp__spacer" />
                <span className="lq-mp__view">{t('marketplace.action.view')}</span>
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}

/* ---------------------------------------------------------------- tab 2: installed */

function Installed({
  plugins,
  installations,
  may,
  onOpen,
}: {
  readonly plugins: ReturnType<typeof usePlugins>;
  readonly installations: ReturnType<typeof useInstallations>;
  readonly may: Permissions;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const setEnabled = useSetInstallationEnabled();
  const uninstall = useUninstallPlugin();
  const [confirming, setConfirming] = useState<string | null>(null);

  if (installations.isError) {
    return (
      <Banner
        title={t('marketplace.error.installations')}
        body={t('marketplace.error.installationsBody')}
      />
    );
  }
  const rows = installations.data ?? [];
  const all = plugins.data ?? [];

  return (
    <>
      {setEnabled.isError ? (
        <div className="lq-mp__note lq-mp__note--error">{setEnabled.error.message}</div>
      ) : null}
      {uninstall.isError ? (
        <div className="lq-mp__note lq-mp__note--error">{uninstall.error.message}</div>
      ) : null}

      {installations.data !== undefined && rows.length === 0 ? (
        <Empty
          title={t('marketplace.installed.empty')}
          body={t('marketplace.installed.emptyGuidance')}
        />
      ) : null}

      {rows.map((installation) => {
        const plugin = all.find((candidate) => candidate.id === installation.plugin_id);
        const upgrade = plugin === undefined ? undefined : upgradeTargetFor(plugin, all);
        const runReason = runBlockedReason(plugin, installation);
        return (
          <div className="lq-mp__card" key={installation.plugin_id}>
            <div className="lq-mp__cardHead">
              <button
                type="button"
                className="lq-mp__link"
                onClick={() => {
                  onOpen(installation.plugin_id);
                }}
              >
                {plugin === undefined
                  ? t('marketplace.installed.unknownPlugin')
                  : `${plugin.name} v${plugin.version}`}
              </button>
              <Pill
                tone={installation.enabled ? 'ok' : 'unknown'}
                label={
                  installation.enabled
                    ? t('marketplace.install.enabled')
                    : t('marketplace.install.disabled')
                }
              />
              {plugin !== undefined && isNarrowed(plugin, installation) ? (
                <span className="lq-mp__metaStrong">
                  {t('marketplace.installed.grantLine', {
                    granted: installation.granted_capabilities.length,
                    declared: plugin.declared_capabilities.length,
                  })}
                </span>
              ) : null}
              {upgrade !== undefined ? (
                <span className="lq-mp__pill lq-mp__pill--sm lq-mp__pill--info">
                  {t('marketplace.installed.upgradeAvailable', {
                    version: upgrade.version,
                  })}
                </span>
              ) : null}
              <span className="lq-mp__spacer" />
              <span className="lq-mp__meta">
                {t('marketplace.installed.by', {
                  who: installation.installed_by,
                  when: new Date(installation.installed_at).toLocaleString(),
                })}
              </span>
            </div>

            <div className="lq-mp__granted">
              {t('marketplace.installed.grantedList', {
                list: installation.granted_capabilities.join(', '),
              })}
            </div>

            <div className="lq-mp__actions">
              <button
                type="button"
                className="lq-mp__btn"
                disabled={!may.run || runReason !== null}
                onClick={() => {
                  onOpen(installation.plugin_id);
                }}
              >
                {t('marketplace.action.run')}
              </button>
              <button
                type="button"
                className="lq-mp__btn"
                disabled={!may.install || setEnabled.isPending}
                onClick={() => {
                  setEnabled.mutate({
                    pluginId: installation.plugin_id,
                    enabled: !installation.enabled,
                  });
                }}
              >
                {installation.enabled
                  ? t('marketplace.action.disable')
                  : t('marketplace.action.enable')}
              </button>
              {upgrade !== undefined ? (
                <button
                  type="button"
                  className="lq-mp__btn"
                  disabled={!may.install}
                  onClick={() => {
                    onOpen(installation.plugin_id);
                  }}
                >
                  {t('marketplace.action.upgradeShort')}
                </button>
              ) : null}

              {/* Inline confirmation, exactly as the artboard has it — not a dialog. */}
              {confirming === installation.plugin_id ? (
                <>
                  <span className="lq-mp__blockBody">
                    {t('marketplace.uninstall.inline')}
                  </span>
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--danger"
                    disabled={uninstall.isPending}
                    onClick={() => {
                      uninstall.mutate(installation.plugin_id, {
                        onSuccess: () => {
                          setConfirming(null);
                        },
                      });
                    }}
                  >
                    {t('marketplace.action.confirm')}
                  </button>
                  <button
                    type="button"
                    className="lq-mp__btn"
                    onClick={() => {
                      setConfirming(null);
                    }}
                  >
                    {t('marketplace.action.cancel')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="lq-mp__btn lq-mp__btn--dangerOutline"
                  disabled={!may.install}
                  onClick={() => {
                    setConfirming(installation.plugin_id);
                  }}
                >
                  {t('marketplace.action.uninstall')}
                </button>
              )}
            </div>

            {runReason !== null ? (
              <div className="lq-mp__reason">{t(runReason)}</div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/* ---------------------------------------------------------------- tab 3: runs */

function Runs({
  plugins,
  may,
}: {
  readonly plugins: ReturnType<typeof usePlugins>;
  readonly may: Permissions;
}): ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [openRun, setOpenRun] = useState<PluginRun | null>(null);
  const runs = useRuns(filter === '' ? null : filter);

  if (openRun !== null) {
    return (
      <RunDetail
        run={openRun}
        onBack={() => {
          setOpenRun(null);
        }}
      />
    );
  }
  if (runs.isError) {
    return (
      <Banner
        title={t('marketplace.error.runs')}
        body={t('marketplace.error.runsBody')}
      />
    );
  }
  const rows = runs.data ?? [];

  return (
    <>
      <div className="lq-mp__toolbar">
        <select
          className="lq-mp__select"
          value={filter}
          aria-label={t('marketplace.runs.filter')}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
        >
          <option value="">{t('marketplace.runs.allPlugins')}</option>
          {(plugins.data ?? []).map((plugin) => (
            <option key={plugin.id} value={plugin.id}>
              {plugin.name} v{plugin.version}
            </option>
          ))}
        </select>
        {!may.run ? (
          <span className="lq-mp__meta">{t('marketplace.runs.noRun')}</span>
        ) : null}
      </div>

      {runs.data !== undefined && rows.length === 0 ? (
        <Empty
          title={t('marketplace.runs.empty')}
          body={t('marketplace.runs.emptyGuidance')}
        />
      ) : null}

      {rows.length > 0 ? (
        <div className="lq-mp__summary">{t('marketplace.runs.capped')}</div>
      ) : null}

      {rows.map((run) => (
        <button
          type="button"
          className="lq-mp__row lq-mp__row--tight"
          key={run.id}
          onClick={() => {
            setOpenRun(run);
          }}
        >
          <span className="lq-mp__hash">{run.id.slice(0, 8)}</span>
          <span className="lq-mp__ver">
            {run.plugin_name} v{run.plugin_version}
          </span>
          <span className="lq-mp__hash">{run.dataset_id}</span>
          <Pill
            small
            tone={runTone(run.status) === 'ok' ? 'ok' : 'error'}
            label={t(`marketplace.runStatus.${run.status}`)}
          />
          {isThinRun(run) ? (
            <span className="lq-mp__thin">{t('marketplace.run.thin')}</span>
          ) : null}
          <span className="lq-mp__spacer" />
          <span className="lq-mp__meta">
            {run.actor} · {new Date(run.ran_at).toLocaleString()}
          </span>
        </button>
      ))}
    </>
  );
}

/** The run detail, inline — the artboard replaces the list rather than opening an overlay. */
function RunDetail({
  run,
  onBack,
}: {
  readonly run: PluginRun;
  readonly onBack: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const findings = useRunFindings(run.id);

  return (
    <>
      <button type="button" className="lq-mp__back" onClick={onBack}>
        {t('marketplace.run.back')}
      </button>
      <div className="lq-mp__panel">
        <div className="lq-mp__panelName">
          {run.plugin_name} v{run.plugin_version}
        </div>
        <div className="lq-mp__panelHash">{run.artifact_hash}</div>

        <div className="lq-mp__grid3">
          <div>
            <div className="lq-mp__factKey">{t('marketplace.run.dataset')}</div>
            <div>{run.dataset_id}</div>
          </div>
          <div>
            <div className="lq-mp__factKey">{t('marketplace.run.actor')}</div>
            <div>{run.actor}</div>
          </div>
          <div>
            <div className="lq-mp__factKey">{t('marketplace.run.ranAt')}</div>
            <div>
              {(() => {
                const date = new Date(run.ran_at);

                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = String(date.getFullYear());

                let hours = date.getHours();
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const ampm = hours >= 12 ? 'PM' : 'AM';

                hours = hours % 12 || 12;

                return `${day}/${month}/${year}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
              })()}
            </div>
          </div>
          <div>
            <div className="lq-mp__factKey">{t('marketplace.run.granted')}</div>
            <div className="lq-mp__factMono">{run.granted_capabilities.join(', ')}</div>
          </div>
          <div>
            <div className="lq-mp__factKey">{t('marketplace.run.used')}</div>
            <div className="lq-mp__factMono">
              {run.capabilities_used.join(', ') || t('marketplace.upgrade.none')}
            </div>
          </div>
          <div>
            <div className="lq-mp__factKey">{t('marketplace.run.duration')}</div>
            <div>{run.duration_ms} ms</div>
          </div>
        </div>

        {isThinRun(run) ? (
          <div className="lq-mp__note">{t('marketplace.run.thinBody')}</div>
        ) : null}

        {run.failure_reason !== null ? (
          <div className="lq-mp__note lq-mp__note--error">
            {t(`marketplace.failure.${run.failure_reason}`)}
          </div>
        ) : null}

        <div className="lq-mp__eyebrow">{t('marketplace.run.findings')}</div>
        {findings.isError ? (
          <div className="lq-mp__reason">{t('marketplace.run.findingsUnread')}</div>
        ) : null}
        {findings.data?.length === 0 ? (
          <div className="lq-mp__reason">{t('marketplace.run.noFindings')}</div>
        ) : null}
        {(findings.data ?? []).map((finding, index) => (
          <div className="lq-mp__finding" key={`${finding.code}-${String(index)}`}>
            {/* Severity is a free-form string the plugin chose, not one of ours — rendered as
                given rather than mapped onto a platform severity it does not mean. */}
            <Pill small tone="unknown" label={finding.severity} />
            <span className="lq-mp__findingCode">{finding.code}</span>
            <span className="lq-mp__findingMsg">{finding.message}</span>
          </div>
        ))}
        {run.findings_truncated ? (
          <div className="lq-mp__after">
            {t('marketplace.run.truncated', { count: run.findings_count })}
          </div>
        ) : null}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- tab 4: my listings */

function Listings({
  may,
  onOpen,
}: {
  readonly may: Permissions;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const listings = useMyListings();
  const [open, setOpen] = useState(false);

  if (listings.isError) {
    return (
      <Banner
        title={t('marketplace.error.listings')}
        body={t('marketplace.error.listingsBody')}
      />
    );
  }
  const rows = listings.data ?? [];

  return (
    <>
      <div className="lq-mp__listHead">
        <span className="lq-mp__listLede">{t('marketplace.listings.scope')}</span>
        <button
          type="button"
          className="lq-mp__btn lq-mp__btn--primary"
          disabled={!may.submit}
          onClick={() => {
            setOpen((was) => !was);
          }}
        >
          {t('marketplace.action.submitPlugin')}
        </button>
      </div>
      {!may.submit ? (
        <div className="lq-mp__submitReason">{t('marketplace.listings.noSubmit')}</div>
      ) : null}

      {open ? (
        <SubmitForm
          onDone={() => {
            setOpen(false);
          }}
        />
      ) : null}

      {listings.data !== undefined && rows.length === 0 ? (
        <Empty
          title={t('marketplace.listings.empty')}
          body={t('marketplace.listings.emptyGuidance')}
        />
      ) : null}

      {rows.map((listing) => (
        <button
          type="button"
          className="lq-mp__row"
          key={listing.id}
          onClick={() => {
            onOpen(listing.id);
          }}
        >
          <span className="lq-mp__ver">
            {listing.name} v{listing.version}
          </span>
          <Pill
            tone={PILL_FOR[stateTone(listing.state)]}
            label={t(`marketplace.state.${listing.state}`)}
          />
          <span className="lq-mp__spacer" />
          <span className="lq-mp__view">{t('marketplace.action.view')}</span>
        </button>
      ))}
    </>
  );
}

const SUBMITTABLE: readonly Capability[] = [
  'dataset.schema',
  'quality.score',
  'rule.results',
];

/** The submission form — a file field and two inputs, never an editor: the artifact is built
 * outside Lumiq and this is the distribution surface. */
function SubmitForm({ onDone }: { readonly onDone: () => void }): ReactElement {
  const { t } = useTranslation();
  const submit = useSubmitPlugin();
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [artifact, setArtifact] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [caps, setCaps] = useState<readonly Capability[]>([]);

  const ready =
    name.trim() !== '' && version.trim() !== '' && artifact !== null && caps.length > 0;

  async function read(file: File): Promise<void> {
    setFileError(null);
    setArtifact(null);
    setHash(null);
    const bytes = new Uint8Array(await file.arrayBuffer());
    /* The ceiling and the magic bytes are the server's own bounds, checked here so an obviously
     * wrong file is refused before it is uploaded rather than after. The server checks both again;
     * this is a courtesy, never the guarantee. */
    if (bytes.byteLength > 8 * 1024 * 1024) {
      setFileError(t('marketplace.submit.tooLarge'));
      return;
    }
    if (!(
      bytes[0] === 0x00 &&
      bytes[1] === 0x61 &&
      bytes[2] === 0x73 &&
      bytes[3] === 0x6d
    )) {
      setFileError(t('marketplace.submit.notWasm'));
      return;
    }
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    setHash(
      [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join(''),
    );
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    setArtifact(btoa(binary));
  }

  return (
    <div className="lq-mp__form lq-mp__form--wide">
      <div className="lq-mp__formTitle lq-mp__formTitle--tight">
        {t('marketplace.submit.title')}
      </div>
      <div className="lq-mp__formSub">{t('marketplace.submit.lede')}</div>

      <div className="lq-mp__fieldGrid">
        <label>
          <span className="lq-mp__label">{t('marketplace.submit.name')}</span>
          <input
            className="lq-mp__input lq-mp__input--full"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </label>
        <label>
          <span className="lq-mp__label">{t('marketplace.submit.version')}</span>
          <input
            className="lq-mp__input lq-mp__input--full lq-mp__input--mono"
            value={version}
            onChange={(event) => {
              setVersion(event.target.value);
            }}
          />
        </label>
      </div>

      <span className="lq-mp__label">{t('marketplace.submit.artifact')}</span>
      <input
        className="lq-mp__file"
        type="file"
        accept=".wasm"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) void read(file);
        }}
      />
      {fileError !== null ? <div className="lq-mp__err">{fileError}</div> : null}
      {hash !== null ? (
        <div className="lq-mp__hashLine">{t('marketplace.submit.hash', { hash })}</div>
      ) : null}

      <div className="lq-mp__capsTitle">{t('marketplace.submit.declared')}</div>
      {SUBMITTABLE.map((code) => (
        <label className="lq-mp__check" key={code}>
          <input
            type="checkbox"
            checked={caps.includes(code)}
            onChange={(event) => {
              setCaps(
                event.target.checked
                  ? [...caps, code]
                  : caps.filter((each) => each !== code),
              );
            }}
          />
          <span>
            <span className="lq-mp__capCode">{code}</span>
            <span className="lq-mp__checkDesc">{t(capabilityLabelKey(code))}</span>
          </span>
        </label>
      ))}
      <div className="lq-mp__foot">{t('marketplace.submit.onlyThree')}</div>

      {submit.isError ? <div className="lq-mp__err">{submit.error.message}</div> : null}

      <div className="lq-mp__btnRow">
        <button
          type="button"
          className="lq-mp__btn lq-mp__btn--primary"
          disabled={!ready || submit.isPending}
          onClick={() => {
            if (artifact === null) return;
            submit.mutate(
              {
                name: name.trim(),
                version: version.trim(),
                artifact,
                capabilities: caps,
              },
              { onSuccess: onDone },
            );
          }}
        >
          {t('marketplace.submit.go')}
        </button>
        <button type="button" className="lq-mp__btn lq-mp__btn--md" onClick={onDone}>
          {t('marketplace.action.cancel')}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- tab 5: review queue */

function ReviewQueue({
  may,
  onOpen,
}: {
  readonly may: Permissions;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const plugins = usePlugins();

  if (!may.review && !may.publish) {
    return (
      <Empty
        title={t('marketplace.review.denied')}
        body={t('marketplace.review.deniedGuidance')}
      />
    );
  }
  const all = plugins.data ?? [];
  const awaitingReview = all.filter((each) => each.review_verdict === 'pending');
  const awaitingPublication = all.filter(
    (each) => each.review_verdict === 'pass' && each.state === 'reviewed',
  );

  if (
    plugins.data !== undefined &&
    awaitingReview.length === 0 &&
    awaitingPublication.length === 0
  ) {
    return (
      <Empty
        title={t('marketplace.review.empty')}
        body={t('marketplace.review.emptyGuidance')}
      />
    );
  }

  const row = (plugin: Plugin, note?: ReactNode): ReactElement => (
    <button
      type="button"
      className="lq-mp__row"
      key={plugin.id}
      onClick={() => {
        onOpen(plugin.id);
      }}
    >
      <span className="lq-mp__ver">
        {plugin.name} v{plugin.version}
      </span>
      <span className="lq-mp__hash" title={plugin.artifact_hash}>
        {plugin.artifact_hash.slice(0, 12)}
      </span>
      {note}
      <span className="lq-mp__spacer" />
      <span className="lq-mp__view">{t('marketplace.action.view')}</span>
    </button>
  );

  return (
    <>
      {awaitingReview.length > 0 ? (
        <>
          <div className="lq-mp__eyebrow">{t('marketplace.review.awaitingReview')}</div>
          {awaitingReview.map((plugin) => row(plugin))}
        </>
      ) : null}
      {awaitingPublication.length > 0 ? (
        <>
          <div className="lq-mp__eyebrow lq-mp__eyebrow--spaced">
            {t('marketplace.review.awaitingPublication')}
          </div>
          {awaitingPublication.map((plugin) =>
            row(
              plugin,
              <span className="lq-mp__metaStrong">
                {t('marketplace.review.twoSignatures')}
              </span>,
            ),
          )}
        </>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- tab 6: activity */

function Activity(): ReactElement {
  const { t } = useTranslation();
  /* The audit chain filtered to this aggregate type — never a second activity store. */
  const activity = useAuditEntries({ aggregateType: 'Plugin' }, null, 50);

  if (activity.isError) {
    return (
      <Banner
        title={t('marketplace.error.activity')}
        body={t('marketplace.error.activityBody')}
      />
    );
  }
  const items = activity.data?.items ?? [];
  if (activity.data !== undefined && items.length === 0) {
    return (
      <Empty
        title={t('marketplace.activity.empty')}
        body={t('marketplace.activity.emptyGuidance')}
      />
    );
  }

  return (
    <div className="lq-mp__table">
      {items.map((entry) => (
        <div
          className="lq-mp__actRow"
          key={`${entry.chain_date}-${String(entry.sequence)}`}
        >
          <span className="lq-mp__actAt">
            {new Date(entry.occurred_at).toLocaleString()}
          </span>
          <span className="lq-mp__actAction">{entry.action}</span>
          <span className="lq-mp__actDetail">
            {t(`marketplace.audit.${entry.action}`)}
          </span>
          <span className="lq-mp__meta">{entry.actor}</span>
        </div>
      ))}
    </div>
  );
}
